// Types for email management system tables
// Source: supabase/migrations/20260324120000_email_management_system.sql

export type EmailStatus = 'pending' | 'sent' | 'failed' | 'bounced' | 'delivered' | 'opened' | 'clicked';
export type EmailRecipientType = 'student' | 'partner' | 'admin' | 'external';
export type EmailCategory = 'onboarding' | 'transactional' | 'notification' | 'reengagement';
export type EmailTemplateRecipientType = 'student' | 'partner' | 'both';
export type EmailTriggerType = 'event' | 'cron' | 'manual';
export type EmailPriority = 'critical' | 'high' | 'medium' | 'low';

export interface EmailLog {
  id: string;
  template_key: string;
  recipient_email: string;
  recipient_name: string | null;
  recipient_type: EmailRecipientType;
  subject: string;
  html_body: string | null;
  status: EmailStatus;
  resend_message_id: string | null;
  error_message: string | null;
  error_code: string | null;
  retry_count: number;
  max_retries: number;
  metadata: Record<string, unknown>;
  triggered_by: string;
  created_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
}

export interface EmailTemplateConfig {
  id: string;
  template_key: string;
  display_name: string;
  description: string;
  category: EmailCategory;
  recipient_type: EmailTemplateRecipientType;
  trigger_type: EmailTriggerType;
  is_active: boolean;
  is_implemented: boolean;
  requires_cron: boolean;
  priority: EmailPriority;
  default_subject_template: string | null;
  variables_schema: Record<string, VariableSchema> | null;
  preview_defaults: Record<string, unknown> | null;
  cooldown_minutes: number;
  total_sent: number;
  total_failed: number;
  total_opened: number;
  last_sent_at: string | null;
  avg_open_rate: number | null;
  created_at: string;
  updated_at: string;
  last_toggled_by: string | null;
  last_toggled_at: string | null;
}

export interface VariableSchema {
  type: 'string' | 'text' | 'email' | 'url' | 'number' | 'boolean' | 'select' | 'array' | 'uuid';
  label: string;
  required?: boolean;
  options?: string[];
}

export interface EmailAutomationRule {
  id: string;
  name: string;
  description: string | null;
  template_key: string;
  trigger_event: string;
  conditions: Record<string, unknown> | null;
  delay_minutes: number;
  is_active: boolean;
  max_sends_per_user: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// RPC return types

export interface EmailDashboardMetrics {
  total_sent: number;
  total_failed: number;
  total_opened: number;
  total_today: number;
  success_rate: number;
  open_rate: number;
  failures_24h: number;
  by_template: TemplateMetric[];
  daily_volume: DailyVolume[];
}

export interface TemplateMetric {
  template_key: string;
  count: number;
  failed: number;
}

export interface DailyVolume {
  date: string;
  sent: number;
  failed: number;
}

// Status badge config
export const EMAIL_STATUS_CONFIG: Record<EmailStatus, { label: string; color: string; icon: string }> = {
  pending:   { label: 'Pendente',  color: 'bg-gray-100 text-gray-700',    icon: 'Clock' },
  sent:      { label: 'Enviado',   color: 'bg-blue-100 text-blue-700',    icon: 'Send' },
  delivered: { label: 'Entregue',  color: 'bg-green-100 text-green-700',  icon: 'CheckCircle2' },
  opened:    { label: 'Aberto',    color: 'bg-emerald-100 text-emerald-700', icon: 'Eye' },
  clicked:   { label: 'Clicado',   color: 'bg-cyan-100 text-cyan-700',    icon: 'MousePointerClick' },
  failed:    { label: 'Falhou',    color: 'bg-red-100 text-red-700',      icon: 'XCircle' },
  bounced:   { label: 'Bounce',    color: 'bg-orange-100 text-orange-700', icon: 'AlertTriangle' },
};

export const EMAIL_CATEGORY_CONFIG: Record<EmailCategory, { label: string; color: string }> = {
  onboarding:    { label: 'Onboarding',    color: 'bg-violet-100 text-violet-700' },
  transactional: { label: 'Transacional',  color: 'bg-blue-100 text-blue-700' },
  notification:  { label: 'Notificação',   color: 'bg-amber-100 text-amber-700' },
  reengagement:  { label: 'Reengajamento', color: 'bg-cyan-100 text-cyan-700' },
};

export const EMAIL_PRIORITY_CONFIG: Record<EmailPriority, { label: string; color: string }> = {
  critical: { label: 'Crítico', color: 'bg-red-100 text-red-700' },
  high:     { label: 'Alta',    color: 'bg-orange-100 text-orange-700' },
  medium:   { label: 'Média',   color: 'bg-blue-100 text-blue-700' },
  low:      { label: 'Baixa',   color: 'bg-gray-100 text-gray-600' },
};
